const DIRECTION_THRESHOLD = 8;
const MOBILE_BREAKPOINT = 740;
const MENU_CLOSE_DURATION = 140;

export function initializePublicNavigation(document: Document, view: Window): () => void {
  const header = document.querySelector<HTMLElement>("[data-sticky-nav]");
  const menu = document.getElementById("mobile-menu");
  const menuToggle = document.getElementById("menu-toggle") as HTMLButtonElement | null;
  if (!header && !menu && !menuToggle) return () => {};

  const initialHeaderEnhanced = header?.classList.contains("is-nav-enhanced") ?? false;
  const initialMenuState = menu
    ? {
        ariaHidden: menu.getAttribute("aria-hidden"),
        hidden: menu.hidden,
        inert: menu.inert ?? false,
      }
    : undefined;
  const initialToggleState = menuToggle
    ? {
        ariaExpanded: menuToggle.getAttribute("aria-expanded"),
        ariaLabel: menuToggle.getAttribute("aria-label"),
      }
    : undefined;

  const cleanup: Array<() => void> = [];
  const listen = (target: EventTarget | null, type: string, listener: EventListener): void => {
    if (!target) return;
    target.addEventListener(type, listener, { passive: type === "scroll" });
    cleanup.push(() => target.removeEventListener(type, listener));
  };

  const reduced = view.matchMedia?.("(prefers-reduced-motion: reduce)");
  let menuCloseTimer: number | undefined;
  const showHeader = (): void => header?.classList.remove("is-nav-hidden");
  const finishMenuClose = (): void => {
    if (!menu) return;
    menu.hidden = true;
    menu.classList.remove("is-closing");
  };
  const closeMenu = (immediate = false): void => {
    if (menuCloseTimer !== undefined) view.clearTimeout(menuCloseTimer);
    menuCloseTimer = undefined;
    if (menu) {
      menu.setAttribute("aria-hidden", "true");
      menu.inert = true;
      menu.classList.remove("is-open");
      if (immediate || reduced?.matches || menu.hidden) finishMenuClose();
      else {
        menu.classList.add("is-closing");
        menuCloseTimer = view.setTimeout(finishMenuClose, MENU_CLOSE_DURATION);
      }
    }
    menuToggle?.setAttribute("aria-expanded", "false");
    menuToggle?.setAttribute("aria-label", "Open navigation");
  };

  listen(menuToggle, "click", () => {
    if (!menu || !menuToggle) return;
    showHeader();
    if (!menu.hidden && !menu.classList.contains("is-closing")) {
      closeMenu();
      return;
    }
    if (menuCloseTimer !== undefined) view.clearTimeout(menuCloseTimer);
    menuCloseTimer = undefined;
    menu.hidden = false;
    menu.inert = false;
    menu.setAttribute("aria-hidden", "false");
    menu.classList.remove("is-closing");
    menu.classList.add("is-open");
    menuToggle.setAttribute("aria-expanded", "true");
    menuToggle.setAttribute("aria-label", "Close navigation");
  });
  menu?.querySelectorAll("a").forEach((link) => listen(link, "click", () => closeMenu(true)));
  listen(document, "keydown", (event) => {
    if ((event as KeyboardEvent).key === "Escape" && menu && !menu.hidden) {
      closeMenu(true);
      menuToggle?.focus();
    }
  });
  listen(view, "resize", () => {
    if (view.innerWidth > MOBILE_BREAKPOINT) closeMenu(true);
  });
  if (header && menu && menuToggle) {
    header.classList.add("is-nav-enhanced");
    closeMenu(true);
  }

  let lastScrollY = Math.max(0, view.scrollY);
  let directionStartY = lastScrollY;
  let direction: -1 | 0 | 1 = 0;
  const resetDirection = (scrollY: number): void => {
    lastScrollY = scrollY;
    directionStartY = scrollY;
    direction = 0;
  };
  const syncScroll = (): void => {
    if (!header) return;
    const currentScrollY = Math.max(0, view.scrollY);
    const menuExpanded = menuToggle?.getAttribute("aria-expanded") === "true";
    const focusInside = header.contains(document.activeElement);
    if (currentScrollY <= header.offsetHeight || menuExpanded || focusInside) {
      showHeader();
      resetDirection(currentScrollY);
      return;
    }

    const delta = currentScrollY - lastScrollY;
    if (delta === 0) return;
    const nextDirection: -1 | 1 = delta > 0 ? 1 : -1;
    if (nextDirection !== direction) {
      direction = nextDirection;
      directionStartY = lastScrollY;
    }
    if (Math.abs(currentScrollY - directionStartY) >= DIRECTION_THRESHOLD)
      header.classList.toggle("is-nav-hidden", direction > 0);
    lastScrollY = currentScrollY;
  };

  listen(view, "scroll", syncScroll);
  listen(header, "focusin", showHeader);

  return () => {
    cleanup.splice(0).forEach((remove) => remove());
    if (menuCloseTimer !== undefined) view.clearTimeout(menuCloseTimer);
    menuCloseTimer = undefined;
    header?.classList.remove("is-nav-hidden");
    header?.classList.toggle("is-nav-enhanced", initialHeaderEnhanced);
    if (menu && initialMenuState) {
      menu.classList.remove("is-open", "is-closing");
      menu.hidden = initialMenuState.hidden;
      menu.inert = initialMenuState.inert;
      if (initialMenuState.ariaHidden === null) menu.removeAttribute("aria-hidden");
      else menu.setAttribute("aria-hidden", initialMenuState.ariaHidden);
    }
    if (menuToggle && initialToggleState) {
      if (initialToggleState.ariaExpanded === null) menuToggle.removeAttribute("aria-expanded");
      else menuToggle.setAttribute("aria-expanded", initialToggleState.ariaExpanded);
      if (initialToggleState.ariaLabel === null) menuToggle.removeAttribute("aria-label");
      else menuToggle.setAttribute("aria-label", initialToggleState.ariaLabel);
    }
  };
}
